import { getMaxCompactThresholdForModel } from "@arti/ai-models-config/contextWindows";
import { supportedReasoningEfforts } from "@arti/ai-models-config/reasoningEffortCapability";
import { SELECTABLE_ACTOR_MODELS } from "@arti/ai-models-config/selectableActorModels";
import { dashboardReasoningEffortSchema } from "@arti/schemas/dashboard";
import { isOneOf } from "@arti/utilities";
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type {
  ActorTemplate,
  CreateTemplateInput,
  ReasoningEffort,
} from "./useTemplates";

import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { useOrgMcpServers } from "../../hooks/useOrgMcpServers";
import { Button } from "../common/Button";
import { SIGNAL_HEX } from "../common/colorTokens";
import { Modal } from "../common/Modal";
import { sliderFillStyle } from "./identity/helpers";
import { getAvatarBackgroundColor, InstanceAvatar } from "./InstanceAvatar";
import { ModelPicker } from "./ModelPicker";

interface TemplateDialogProps {
  /** If provided, editing existing template; otherwise creating new. */
  template?: ActorTemplate;
  onClose: () => void;
  onSave: (input: CreateTemplateInput) => Promise<void>;
  /** Revert an overridden template back to its seed defaults. Only surfaced
   *  as a button when the admin is editing source="overridden". */
  onRevertToSeed?: () => Promise<void>;
  saving: boolean;
}

export const TemplateDialog: FC<TemplateDialogProps> = ({
  template,
  onClose,
  onSave,
  onRevertToSeed,
  saving,
}) => {
  const [name, setName] = useState(template?.name ?? "");
  const [description, setDescription] = useState(template?.description ?? "");
  const [avatarSeed, setAvatarSeed] = useState(template?.avatarSeed ?? "");
  const [modelRef, setModelRef] = useState(template?.modelRef ?? "");
  const [reasoningEffort, setReasoningEffort] = useState<"" | ReasoningEffort>(
    template?.reasoningEffort ?? "",
  );
  const [identityPrompt, setIdentityPrompt] = useState(
    template?.identityPrompt ?? "",
  );
  const [tickIntervalMs, setTickIntervalMs] = useState(
    template?.tickIntervalMs != null ? String(template.tickIntervalMs) : "",
  );
  const [compactThreshold, setCompactThreshold] = useState(
    template?.compactContextGrowthThreshold != null
      ? String(template.compactContextGrowthThreshold)
      : "20000",
  );
  const [heartbeatIntervalS, setHeartbeatIntervalS] = useState(
    template?.heartbeatIntervalS != null
      ? String(template.heartbeatIntervalS)
      : "",
  );
  const [unarmedParkGraceS, setUnarmedParkGraceS] = useState(
    template?.unarmedParkGraceS != null
      ? String(template.unarmedParkGraceS)
      : "",
  );
  const [consolidationSchedule, setConsolidationSchedule] = useState(
    template?.consolidationSchedule ?? "",
  );
  const [isSingleton, setIsSingleton] = useState(
    template?.isSingleton ?? false,
  );
  const [mcpServerIds, setMcpServerIds] = useState<string[]>(
    template?.mcpServerIds ?? [],
  );
  const [issueWorkspaceDefault, setIssueWorkspaceDefault] = useState<
    "" | "lead" | "member"
  >(template?.issueWorkspaceDefault ?? "");

  const effortOptions = useMemo(
    () => supportedReasoningEfforts(modelRef),
    [modelRef],
  );
  const selectedEffort = reasoningEffort;
  const effortUnsupported =
    reasoningEffort !== "" &&
    !effortOptions.some((option) => option.value === selectedEffort);
  const previousModelRef = useRef(modelRef);
  useEffect(() => {
    if (previousModelRef.current === modelRef) {
      return;
    }
    previousModelRef.current = modelRef;
    if (effortUnsupported) {
      setReasoningEffort("");
    }
  }, [modelRef, effortUnsupported]);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description ?? "");
      setAvatarSeed(template.avatarSeed);
      setModelRef(template.modelRef ?? "");
      previousModelRef.current = template.modelRef ?? "";
      setReasoningEffort(template.reasoningEffort ?? "");
      setIdentityPrompt(template.identityPrompt ?? "");
      setTickIntervalMs(
        template.tickIntervalMs != null ? String(template.tickIntervalMs) : "",
      );
      setCompactThreshold(String(template.compactContextGrowthThreshold));
      setHeartbeatIntervalS(
        template.heartbeatIntervalS != null
          ? String(template.heartbeatIntervalS)
          : "",
      );
      setUnarmedParkGraceS(
        template.unarmedParkGraceS != null
          ? String(template.unarmedParkGraceS)
          : "",
      );
      setConsolidationSchedule(template.consolidationSchedule ?? "");
      setIsSingleton(template.isSingleton);
      setMcpServerIds(template.mcpServerIds ?? []);
      setIssueWorkspaceDefault(template.issueWorkspaceDefault ?? "");
    }
  }, [template]);

  const handleSave = useCallback(async () => {
    const trimmedName = name.trim();
    const trimmedSeed = avatarSeed.trim();
    if (!trimmedName || !trimmedSeed) {
      return;
    }
    const tickMs = tickIntervalMs.trim()
      ? parseInt(tickIntervalMs.trim(), 10)
      : undefined;
    const compactThresholdN = compactThreshold.trim()
      ? parseInt(compactThreshold.trim(), 10)
      : undefined;
    const heartbeatN = heartbeatIntervalS.trim()
      ? parseInt(heartbeatIntervalS.trim(), 10)
      : undefined;
    const unarmedGraceN = unarmedParkGraceS.trim()
      ? parseInt(unarmedParkGraceS.trim(), 10)
      : undefined;
    await onSave({
      avatarSeed: trimmedSeed,
      compactContextGrowthThreshold: compactThresholdN,
      consolidationSchedule: consolidationSchedule.trim() || undefined,
      description: description.trim() || undefined,
      heartbeatIntervalS: heartbeatN,
      identityPrompt: identityPrompt.trim() || undefined,
      isSingleton,
      issueWorkspaceDefault: issueWorkspaceDefault || null,
      mcpServerIds,
      modelRef: modelRef || undefined,
      name: trimmedName,
      reasoningEffort: reasoningEffort || null,
      tickIntervalMs: tickMs,
      unarmedParkGraceS: unarmedGraceN,
    });
  }, [
    avatarSeed,
    compactThreshold,
    consolidationSchedule,
    description,
    heartbeatIntervalS,
    identityPrompt,
    isSingleton,
    issueWorkspaceDefault,
    mcpServerIds,
    modelRef,
    name,
    onSave,
    reasoningEffort,
    tickIntervalMs,
    unarmedParkGraceS,
  ]);

  const isEditing = Boolean(template);
  const canSave = name.trim().length > 0 && avatarSeed.trim().length > 0;

  const accentColor = useMemo(
    () =>
      avatarSeed.trim().length > 0
        ? getAvatarBackgroundColor(avatarSeed.trim())
        : SIGNAL_HEX.accent,
    [avatarSeed],
  );

  const maxCompactThreshold = useMemo(
    // Empty modelRef = "default" = Claude Sonnet 4.6 — resolve explicitly so
    // The slider reflects 1M instead of the 128K unknown-model fallback.
    () => getMaxCompactThresholdForModel(modelRef || "claude-sonnet-4-6"),
    [modelRef],
  );
  const compactThresholdN = (() => {
    const parsed = Number.parseInt(compactThreshold, 10);
    if (!Number.isFinite(parsed) || parsed < 1000) {
      return 1000;
    }
    return Math.min(parsed, maxCompactThreshold);
  })();

  useEffect(() => {
    const parsed = Number.parseInt(compactThreshold, 10);
    if (Number.isFinite(parsed) && parsed > maxCompactThreshold) {
      setCompactThreshold(String(maxCompactThreshold));
    }
  }, [compactThreshold, maxCompactThreshold]);

  // Editing a platform seed routes through the override path on save — show
  // The user that fact so the semantics aren't surprising.
  const isPlatform = template?.scope === "platform";
  const subtitle = (() => {
    if (!isEditing || !template) {
      return "Define a reusable actor configuration";
    }
    if (!isPlatform) {
      return `Editing "${template.name}"`;
    }
    if (template.source === "overridden") {
      return `Editing override for seeded "${template.name}" (seed preserved)`;
    }
    return `Editing seeded "${template.name}" — save will create an override`;
  })();

  return (
    <Modal
      title={isEditing ? "Edit Template" : "New Template"}
      subtitle={subtitle}
      onClose={onClose}
      widthClass="max-w-4xl"
      footer={
        <div className="flex items-center justify-between gap-2">
          <div>
            {onRevertToSeed && template?.source === "overridden" && (
              <Button
                variant="secondary"
                size="sm"
                disabled={saving}
                onClick={() => void onRevertToSeed()}
                title="Delete this org's override and fall back to the seeded template defaults."
              >
                Reset to seed defaults
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => void handleSave()}
              disabled={!canSave || saving}
            >
              {saving ? "Saving…" : saveLabel(isEditing)}
            </Button>
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-4 py-1 md:grid-cols-[260px_1fr]">
        {/* ── Left: character panel (retro char-select vibe) ────────────── */}
        <CharacterPanel
          accentColor={accentColor}
          avatarSeed={avatarSeed}
          setAvatarSeed={setAvatarSeed}
          modelLabel={
            SELECTABLE_ACTOR_MODELS.find((m) => m.ref === modelRef)?.label ??
            "default"
          }
          tickIntervalMs={tickIntervalMs}
          heartbeatIntervalS={heartbeatIntervalS}
          unarmedParkGraceS={unarmedParkGraceS}
          compactThresholdN={compactThresholdN}
          maxCompactThreshold={maxCompactThreshold}
          isSingleton={isSingleton}
          mcpServerIds={mcpServerIds}
          setMcpServerIds={setMcpServerIds}
          setIsSingleton={setIsSingleton}
        />

        {/* ── Right: form fields ─────────────────────────────────────────── */}
        <div className="flex flex-col gap-3">
          {/* Name */}
          <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700/80 dark:text-emerald-300/70">
              [ NAME ] <span className="text-rose-400">*</span>
            </label>
            <input
              className="w-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 outline-none focus:border-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:border-slate-500"
              placeholder="e.g. Research Agent"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700/80 dark:text-emerald-300/70">
              [ DESCRIPTION ]
            </label>
            <input
              className="w-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 outline-none focus:border-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:border-slate-500"
              placeholder="Optional description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Model */}
          <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700/80 dark:text-emerald-300/70">
              [ MODEL ]
            </label>
            <ModelPicker
              value={modelRef}
              onChange={setModelRef}
              noneOptionLabel="Runtime default (Claude Sonnet 4.6)"
            />
          </div>

          {/* Reasoning effort */}
          <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700/80 dark:text-emerald-300/70">
              [ REASONING EFFORT ]
            </label>
            <select
              className="w-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 outline-none focus:border-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:border-slate-500"
              value={selectedEffort}
              onChange={(e) => {
                const { value } = e.target;
                setReasoningEffort(
                  value === ""
                    ? value
                    : dashboardReasoningEffortSchema.parse(value),
                );
              }}
            >
              <option value="">inherit (stage default)</option>
              {effortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
              {effortUnsupported && (
                <option value={reasoningEffort}>
                  {reasoningEffort} (unsupported by this model)
                </option>
              )}
            </select>
          </div>

          {/* Issue workspace role */}
          <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700/80 dark:text-emerald-300/70">
              [ ISSUE WORKSPACE ROLE ]
            </label>
            <select
              className="w-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 outline-none focus:border-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:border-slate-500"
              value={issueWorkspaceDefault}
              onChange={(e) => {
                const { value } = e.target;
                if (isOneOf(value, ["", "lead", "member"] as const)) {
                  setIssueWorkspaceDefault(value);
                }
              }}
            >
              <option value="">none</option>
              <option value="lead">
                lead — preselected as issue-workspace lead (one per org)
              </option>
              <option value="member">
                member — preselected as issue-workspace member
              </option>
            </select>
          </div>

          {/* Identity Prompt */}
          <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-violet-600/80 dark:text-violet-300/80">
              [ IDENTITY PROMPT ]
            </label>
            <textarea
              className="h-20 w-full resize-none border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 outline-none focus:border-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:border-slate-500"
              placeholder="System prompt applied when spawning from this template…"
              value={identityPrompt}
              onChange={(e) => setIdentityPrompt(e.target.value)}
            />
          </div>

          {/* Tick Interval */}
          <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700/80 dark:text-emerald-300/70">
              [ TICK INTERVAL · MS ]
            </label>
            <TimeChipRow
              options={[
                { label: "default", value: "" },
                { label: "5s", value: "5000" },
                { label: "15s", value: "15000" },
                { label: "60s", value: "60000" },
              ]}
              value={tickIntervalMs}
              onPick={setTickIntervalMs}
            />
            <input
              className="w-full border border-slate-200 bg-white px-3 py-1.5 font-mono text-sm text-slate-900 outline-none focus:border-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:border-slate-500"
              placeholder="e.g. 15000"
              type="number"
              min={1000}
              value={tickIntervalMs}
              onChange={(e) => setTickIntervalMs(e.target.value)}
            />
          </div>

          {/* Compact threshold */}
          <div className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between">
              <label className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700/80 dark:text-emerald-300/70">
                [ COMPACT THRESHOLD ]
              </label>
              <span className="font-mono text-[10px] text-slate-400">
                {formatTokenCount(compactThresholdN)} /{" "}
                {formatTokenCount(maxCompactThreshold)} max
              </span>
            </div>
            <input
              type="range"
              min={1000}
              max={maxCompactThreshold}
              step={1000}
              value={compactThresholdN}
              onChange={(e) => setCompactThreshold(e.target.value)}
              className="slider-square w-full"
              style={sliderFillStyle(
                compactThresholdN,
                1000,
                maxCompactThreshold,
              )}
            />
            <p className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
              When context_growth_since_evolution crosses this, the actor
              force-compacts. Cap: 90% of the model's context window.
            </p>
          </div>

          {/* Heartbeat interval */}
          <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700/80 dark:text-emerald-300/70">
              [ HEARTBEAT · SECONDS ]
            </label>
            <TimeChipRow
              options={[
                { label: "off", value: "" },
                { label: "30s", value: "30" },
                { label: "5m", value: "300" },
                { label: "1h", value: "3600" },
              ]}
              value={heartbeatIntervalS}
              onPick={setHeartbeatIntervalS}
            />
            <input
              className="w-full border border-slate-200 bg-white px-3 py-1.5 font-mono text-sm text-slate-900 outline-none focus:border-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:border-slate-500"
              placeholder="blank = no heartbeat"
              type="number"
              min={5}
              max={3600}
              step={5}
              value={heartbeatIntervalS}
              onChange={(e) => setHeartbeatIntervalS(e.target.value)}
            />
          </div>

          {/* Unarmed park grace */}
          <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700/80 dark:text-emerald-300/70">
              [ UNARMED PARK GRACE · SECONDS ]
            </label>
            <TimeChipRow
              options={[
                { label: "default 15m", value: "" },
                { label: "5m", value: "300" },
                { label: "30m", value: "1800" },
                { label: "1h", value: "3600" },
              ]}
              value={unarmedParkGraceS}
              onPick={setUnarmedParkGraceS}
            />
            <input
              className="w-full border border-slate-200 bg-white px-3 py-1.5 font-mono text-sm text-slate-900 outline-none focus:border-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:border-slate-500"
              placeholder="blank = 900"
              type="number"
              min={1}
              step={60}
              value={unarmedParkGraceS}
              onChange={(e) => setUnarmedParkGraceS(e.target.value)}
            />
          </div>

          {/* Consolidation schedule */}
          <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700/80 dark:text-emerald-300/70">
              [ CONSOLIDATION SCHEDULE ]
            </label>
            <input
              className="w-full border border-slate-200 bg-white px-3 py-1.5 font-mono text-sm text-slate-900 outline-none focus:border-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:border-slate-500"
              placeholder="interval:180 | daily:03:00 | cron:0 3 * * *"
              value={consolidationSchedule}
              onChange={(e) => setConsolidationSchedule(e.target.value)}
            />
            <p className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
              Grammar: interval:&lt;seconds&gt; | daily:HH:MM[,HH:MM][@tz] |
              cron:&lt;5-field&gt;. Blank = no consolidation.
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// CharacterPanel — retro character-creator framing for the avatar preview
// ---------------------------------------------------------------------------

interface CharacterPanelProps {
  accentColor: string;
  avatarSeed: string;
  setAvatarSeed: (v: string) => void;
  modelLabel: string;
  tickIntervalMs: string;
  heartbeatIntervalS: string;
  unarmedParkGraceS: string;
  compactThresholdN: number;
  maxCompactThreshold: number;
  isSingleton: boolean;
  mcpServerIds: string[];
  setIsSingleton: (v: boolean) => void;
  setMcpServerIds: (ids: string[]) => void;
}

const CharacterPanel: FC<CharacterPanelProps> = ({
  accentColor,
  avatarSeed,
  setAvatarSeed,
  modelLabel,
  tickIntervalMs,
  heartbeatIntervalS,
  unarmedParkGraceS,
  compactThresholdN,
  maxCompactThreshold,
  isSingleton,
  mcpServerIds,
  setIsSingleton,
  setMcpServerIds,
}) => {
  const hasSeed = avatarSeed.trim().length > 0;
  // 8-digit alpha suffix for tinted surfaces (~10% fill, ~30% border).
  const tintFill = `${accentColor}1a`;
  const tintBorder = `${accentColor}4d`;

  return (
    <div className="flex flex-col gap-3">
      {/* Section header */}
      <p
        className="font-mono text-[10px] uppercase tracking-widest"
        style={{ color: accentColor }}
      >
        [ character ]
      </p>

      {/* Avatar frame — brutalist, pixel-grid backdrop, corner brackets */}
      <div
        className="relative flex aspect-square w-full items-center justify-center border-2"
        style={{
          backgroundColor: tintFill,
          backgroundImage: `
            linear-gradient(${accentColor}26 1px, transparent 1px),
            linear-gradient(90deg, ${accentColor}26 1px, transparent 1px)
          `,
          backgroundSize: "8px 8px",
          borderColor: accentColor,
          boxShadow: `inset 0 0 24px ${accentColor}33, 0 0 16px ${accentColor}1a`,
          imageRendering: "pixelated",
        }}
      >
        {/* Corner pixel brackets (Tetris/8-bit vibe) */}
        <CornerBracket position="tl" color={accentColor} />
        <CornerBracket position="tr" color={accentColor} />
        <CornerBracket position="bl" color={accentColor} />
        <CornerBracket position="br" color={accentColor} />

        {/* CRT scanline overlay */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 3px)",
          }}
        />

        {/* Avatar, scaled up for hero display */}
        {hasSeed ? (
          <div className="scale-[3.2]">
            <InstanceAvatar seed={avatarSeed} status="waiting" animated />
          </div>
        ) : (
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700/80 dark:text-emerald-300/70">
            ? enter seed ?
          </p>
        )}
      </div>

      {/* Codename (seed) input — labeled in-character */}
      <div className="flex flex-col gap-1">
        <label
          className="font-mono text-[10px] uppercase tracking-widest"
          style={{ color: accentColor }}
        >
          Codename <span className="text-rose-400">*</span>
        </label>
        <input
          className="w-full border bg-white px-3 py-1.5 font-mono text-sm text-slate-900 outline-none dark:bg-slate-800 dark:text-white"
          style={{ borderColor: tintBorder }}
          placeholder="e.g. research-v1"
          value={avatarSeed}
          onChange={(e) => setAvatarSeed(e.target.value)}
        />
      </div>

      {/* Stat readouts — derived from other form fields, update live */}
      <div
        className="flex flex-col gap-1.5 border px-3 py-2.5"
        style={{ backgroundColor: `${accentColor}0d`, borderColor: tintBorder }}
      >
        <p
          className="mb-0.5 font-mono text-[9px] uppercase tracking-widest opacity-70"
          style={{ color: accentColor }}
        >
          [ stats ]
        </p>
        <StatRow
          icon="◉"
          label="MODEL"
          value={modelLabel.replace(" (default)", "")}
          accentColor={accentColor}
        />
        <StatRow
          icon="▲"
          label="TICK"
          value={tickIntervalMs ? `${tickIntervalMs}ms` : "—"}
          accentColor={accentColor}
        />
        <StatRow
          icon="♥"
          label="BEAT"
          value={heartbeatIntervalS ? `${heartbeatIntervalS}s` : "—"}
          accentColor={accentColor}
        />
        <StatRow
          icon="⌁"
          label="PARK"
          value={unarmedParkGraceS ? `${unarmedParkGraceS}s` : "900s"}
          accentColor={accentColor}
        />
        <StatRow
          icon="▦"
          label="BUF"
          value={`${formatTokenCount(compactThresholdN)} / ${formatTokenCount(maxCompactThreshold)}`}
          accentColor={accentColor}
        />
      </div>

      {/* MCP servers — which of the organization's registered tool servers this
          template's actors reach. Rendered in this dialog's own idiom rather
          than the settings one, because it lives inside the dialog. */}
      <McpServerAttachments
        accentColor={accentColor}
        onChange={setMcpServerIds}
        selected={mcpServerIds}
        tintBorder={tintBorder}
      />

      {/* Mode toggle — solo (singleton) vs party (multi-instance) */}
      <button
        type="button"
        onClick={() => setIsSingleton(!isSingleton)}
        className="group flex items-center justify-between border px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors"
        style={{
          backgroundColor: isSingleton ? `${accentColor}26` : "transparent",
          borderColor: tintBorder,
          color: accentColor,
        }}
        title={
          isSingleton
            ? "Singleton: at most one live instance per org. Spawn blocks if one already exists."
            : "Multi: any number of concurrent instances."
        }
      >
        <span className="flex items-center gap-1.5">
          <span>{isSingleton ? "▣" : "▢"}</span>
          <span>{isSingleton ? "[ solo mode ]" : "[ party mode ]"}</span>
        </span>
        <span className="opacity-60">{isSingleton ? "1 max" : "∞"}</span>
      </button>
    </div>
  );
};

const CornerBracket: FC<{
  position: "tl" | "tr" | "bl" | "br";
  color: string;
}> = ({ position, color }) => {
  const pos = {
    bl: "bottom-0 left-0",
    br: "bottom-0 right-0",
    tl: "left-0 top-0",
    tr: "right-0 top-0",
  }[position];
  return (
    <span
      aria-hidden
      className={`absolute h-2 w-2 ${pos}`}
      style={{ backgroundColor: color }}
    />
  );
};

/**
 * Which org MCP servers a template attaches. Disabled registrations are not
 * offered -- attaching one would look like it works and resolve to nothing --
 * but one that is already attached is still listed, so an admin can see why an
 * actor lost its tools instead of finding a silently shorter list.
 */
const McpServerAttachments: FC<{
  accentColor: string;
  onChange: (ids: string[]) => void;
  selected: string[];
  tintBorder: string;
}> = ({ accentColor, onChange, selected, tintBorder }) => {
  const auth = useDashboardAuth();
  const { servers, error } = useOrgMcpServers(auth.organizationId);
  const offered = servers.filter(
    (server) => server.status === "active" || selected.includes(server.id),
  );

  // Nothing registered and nothing attached is not a control worth showing: an
  // empty list of checkboxes tells a reader less than saying where they come
  // from, and this dialog is already dense.
  if (offered.length === 0) {
    return null;
  }

  const toggle = (id: string) => {
    onChange(
      selected.includes(id)
        ? selected.filter((current) => current !== id)
        : [...selected, id],
    );
  };

  return (
    <div
      className="flex flex-col gap-1.5 border px-3 py-2.5"
      style={{ backgroundColor: `${accentColor}0d`, borderColor: tintBorder }}
    >
      <p
        className="mb-0.5 font-mono text-[9px] uppercase tracking-widest opacity-70"
        style={{ color: accentColor }}
      >
        [ mcp servers ]
      </p>
      {error && (
        <p className="font-mono text-[9px] text-amber-600 dark:text-amber-500">
          {error}
        </p>
      )}
      {offered.map((server) => {
        const attached = selected.includes(server.id);
        return (
          <button
            className="flex items-center justify-between gap-2 font-mono text-[10px] transition-colors"
            key={server.id}
            onClick={() => toggle(server.id)}
            title={server.url}
            type="button"
          >
            <span className="flex min-w-0 items-center gap-1.5">
              <span style={{ color: attached ? accentColor : undefined }}>
                {attached ? "▣" : "▢"}
              </span>
              <span
                className={`truncate uppercase tracking-widest ${
                  attached
                    ? "text-slate-700 dark:text-slate-200"
                    : "text-slate-500 dark:text-slate-400"
                }`}
              >
                {server.display_name}
              </span>
            </span>
            <span className="shrink-0 text-slate-400 dark:text-slate-500">
              {server.status === "disabled" ? "disabled" : server.slug}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const StatRow: FC<{
  icon: string;
  label: string;
  value: string;
  accentColor: string;
}> = ({ icon, label, value, accentColor }) => (
  <div className="flex items-center justify-between gap-2 font-mono text-[10px]">
    <span className="flex items-center gap-1.5">
      <span style={{ color: accentColor }}>{icon}</span>
      <span className="uppercase tracking-widest text-slate-500 dark:text-slate-400">
        {label}
      </span>
    </span>
    <span className="truncate tabular-nums text-slate-700 dark:text-slate-200">
      {value}
    </span>
  </div>
);

function saveLabel(isEditing: boolean): string {
  return isEditing ? "Save Changes" : "Create Template";
}

function formatTokenCount(n: number): string {
  if (n >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  if (n >= 1000) {
    return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
  }
  return String(n);
}

/** Quick-pick chips over a raw seconds/ms input: clicking a chip writes the
 *  string state ("" = the field's blank semantics); typing custom values in
 *  the input still works — the chips just remove the unit arithmetic. */
function TimeChipRow({
  options,
  value,
  onPick,
}: {
  options: { label: string; value: string }[];
  value: string;
  onPick: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((option) => {
        const active = value.trim() === option.value;
        return (
          <button
            key={option.label}
            type="button"
            onClick={() => onPick(option.value)}
            className={`border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest transition-colors ${
              active
                ? "border-emerald-400 bg-emerald-400/10 text-emerald-700 dark:text-emerald-300"
                : "border-slate-300 text-slate-500 hover:border-emerald-400/50 hover:text-emerald-600 dark:border-slate-700 dark:text-slate-400 dark:hover:text-emerald-300"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
